import React from 'react';

// Cute animated SVG icons - all laundry-themed, consistent teal style
// Self-hosted, no external dependencies, guaranteed to load

const BRAND = '#26BCC1';
const BRAND_LIGHT = '#A8E6E8';
const BRAND_DARK = '#178A8E';
const BUBBLE = '#E9FAFB';
const INK = '#0F2A2C';

// 1. Daily Kiloan - Washing machine with spinning drum + bubbles
export const WashingMachineIcon = () => (
  <svg viewBox="0 0 200 200" className="w-full h-full" aria-hidden="true">
    <defs>
      <radialGradient id="wm-water" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor={BRAND_LIGHT} />
        <stop offset="100%" stopColor={BRAND} />
      </radialGradient>
    </defs>
    {/* Floating bubbles */}
    <circle cx="40" cy="50" r="4" fill={BRAND} opacity=".5">
      <animate attributeName="cy" values="50;30;50" dur="3s" repeatCount="indefinite" />
      <animate attributeName="opacity" values=".5;0;.5" dur="3s" repeatCount="indefinite" />
    </circle>
    <circle cx="160" cy="60" r="3" fill={BRAND} opacity=".5">
      <animate attributeName="cy" values="60;35;60" dur="2.5s" repeatCount="indefinite" />
      <animate attributeName="opacity" values=".5;0;.5" dur="2.5s" repeatCount="indefinite" />
    </circle>
    <circle cx="170" cy="45" r="5" fill={BRAND} opacity=".4">
      <animate attributeName="cy" values="45;20;45" dur="3.5s" repeatCount="indefinite" />
      <animate attributeName="opacity" values=".4;0;.4" dur="3.5s" repeatCount="indefinite" />
    </circle>
    {/* Body */}
    <rect x="50" y="55" width="100" height="120" rx="10" fill="#fff" stroke={INK} strokeWidth="3" />
    {/* Control panel */}
    <rect x="60" y="65" width="80" height="18" rx="4" fill={BUBBLE} />
    <circle cx="125" cy="74" r="4" fill={BRAND}>
      <animate attributeName="fill" values={`${BRAND};${BRAND_DARK};${BRAND}`} dur="2s" repeatCount="indefinite" />
    </circle>
    <rect x="68" y="71" width="22" height="6" rx="2" fill={INK} opacity=".3" />
    {/* Door outer */}
    <circle cx="100" cy="125" r="38" fill={BRAND_LIGHT} stroke={INK} strokeWidth="3" />
    <circle cx="100" cy="125" r="30" fill="url(#wm-water)" />
    {/* Spinning drum (clothes inside) */}
    <g style={{ transformOrigin: '100px 125px', animation: 'wm-spin 3s linear infinite' }}>
      <rect x="78" y="110" width="20" height="14" rx="3" fill="#FFD166" />
      <rect x="100" y="118" width="18" height="12" rx="3" fill="#F87171" />
      <rect x="88" y="130" width="22" height="13" rx="3" fill="#60A5FA" />
    </g>
    {/* Door highlight */}
    <ellipse cx="88" cy="113" rx="10" ry="6" fill="#fff" opacity=".5" />
  </svg>
);

// 2. Cuci & Setrika - Iron with steam
export const IronIcon = () => (
  <svg viewBox="0 0 200 200" className="w-full h-full" aria-hidden="true">
    {/* Steam puffs */}
    {[0, 1, 2].map((i) => (
      <g key={i} style={{ animation: `steam-rise 2.4s ${i * 0.4}s ease-out infinite` }}>
        <circle cx={70 + i * 25} cy="50" r="6" fill={BRAND_LIGHT} opacity=".7" />
      </g>
    ))}
    {/* Ironing board */}
    <ellipse cx="100" cy="160" rx="80" ry="8" fill={BUBBLE} />
    <rect x="20" y="140" width="160" height="14" rx="7" fill="#fff" stroke={INK} strokeWidth="3" />
    {/* Iron body */}
    <g style={{ transformOrigin: '100px 120px', animation: 'iron-glide 2.6s ease-in-out infinite' }}>
      <path d="M 50 130 L 150 130 L 145 105 Q 100 90 55 105 Z" fill={BRAND} stroke={INK} strokeWidth="3" strokeLinejoin="round" />
      {/* Handle */}
      <path d="M 75 105 Q 100 75 125 105" fill="none" stroke={INK} strokeWidth="4" strokeLinecap="round" />
      <rect x="80" y="92" width="40" height="8" rx="4" fill={INK} />
      {/* Tip details */}
      <circle cx="100" cy="120" r="4" fill="#fff" />
      <circle cx="80" cy="120" r="3" fill="#fff" opacity=".7" />
      <circle cx="120" cy="120" r="3" fill="#fff" opacity=".7" />
    </g>
  </svg>
);

// 3. Bed Cover - bed with floating cover
export const BedCoverIcon = () => (
  <svg viewBox="0 0 200 200" className="w-full h-full" aria-hidden="true">
    {/* Sparkles */}
    {[{x:35,y:55,d:0},{x:165,y:60,d:.6},{x:160,y:130,d:1.2}].map((s,i)=>(
      <g key={i} style={{ transformOrigin: `${s.x}px ${s.y}px`, animation: `sparkle 2s ${s.d}s ease-in-out infinite` }}>
        <path d={`M ${s.x} ${s.y-6} L ${s.x+2} ${s.y-2} L ${s.x+6} ${s.y} L ${s.x+2} ${s.y+2} L ${s.x} ${s.y+6} L ${s.x-2} ${s.y+2} L ${s.x-6} ${s.y} L ${s.x-2} ${s.y-2} Z`} fill={BRAND} />
      </g>
    ))}
    {/* Headboard */}
    <rect x="20" y="60" width="160" height="20" rx="6" fill={BRAND} stroke={INK} strokeWidth="3" />
    {/* Bed base */}
    <rect x="15" y="140" width="170" height="35" rx="6" fill="#fff" stroke={INK} strokeWidth="3" />
    {/* Mattress */}
    <rect x="20" y="115" width="160" height="30" rx="6" fill="#fff" stroke={INK} strokeWidth="3" />
    {/* Bed cover - floats up and down */}
    <g style={{ animation: 'bed-float 3s ease-in-out infinite' }}>
      <path d="M 25 130 Q 60 118 100 128 T 175 130 L 175 145 L 25 145 Z" fill={BRAND_LIGHT} stroke={INK} strokeWidth="3" strokeLinejoin="round" />
      <path d="M 35 135 L 55 135 M 75 135 L 95 135 M 115 135 L 135 135 M 155 135 L 170 135" stroke={BRAND} strokeWidth="2" strokeLinecap="round" />
    </g>
    {/* Pillows */}
    <rect x="30" y="100" width="50" height="22" rx="6" fill={BRAND_LIGHT} stroke={INK} strokeWidth="3" />
    <rect x="90" y="100" width="50" height="22" rx="6" fill={BRAND_LIGHT} stroke={INK} strokeWidth="3" />
    {/* Legs */}
    <rect x="20" y="172" width="8" height="14" fill={INK} />
    <rect x="172" y="172" width="8" height="14" fill={INK} />
  </svg>
);

// 4. Laundry Tas - bag with swing
export const BagIcon = () => (
  <svg viewBox="0 0 200 200" className="w-full h-full" aria-hidden="true">
    {/* Sparkles */}
    {[{x:40,y:60},{x:160,y:70},{x:35,y:140}].map((s,i)=>(
      <circle key={i} cx={s.x} cy={s.y} r="3" fill={BRAND} opacity=".6">
        <animate attributeName="r" values="2;5;2" dur="2s" begin={`${i*0.3}s`} repeatCount="indefinite" />
      </circle>
    ))}
    {/* Hook */}
    <line x1="100" y1="30" x2="100" y2="55" stroke={INK} strokeWidth="3" strokeLinecap="round" />
    <circle cx="100" cy="28" r="4" fill={INK} />
    {/* Bag - swings */}
    <g style={{ transformOrigin: '100px 55px', animation: 'bag-swing 3s ease-in-out infinite' }}>
      {/* Handles */}
      <path d="M 70 70 Q 70 45 100 45 Q 130 45 130 70" fill="none" stroke={INK} strokeWidth="4" strokeLinecap="round" />
      {/* Bag body */}
      <path d="M 55 70 L 145 70 L 155 165 Q 100 175 45 165 Z" fill={BRAND} stroke={INK} strokeWidth="3" strokeLinejoin="round" />
      {/* Front flap */}
      <path d="M 75 70 L 125 70 L 130 105 Q 100 112 70 105 Z" fill={BRAND_DARK} stroke={INK} strokeWidth="2.5" strokeLinejoin="round" />
      {/* Button */}
      <circle cx="100" cy="92" r="6" fill="#FFD166" stroke={INK} strokeWidth="2" />
    </g>
  </svg>
);

// 5. Laundry Karpet - rolled carpet
export const CarpetIcon = () => (
  <svg viewBox="0 0 200 200" className="w-full h-full" aria-hidden="true">
    {/* Sparkles */}
    {[0,1,2].map((i)=>(
      <circle key={i} cx={40+i*60} cy={45} r="3" fill={BRAND}>
        <animate attributeName="cy" values="45;25;45" dur={`${2+i*0.3}s`} repeatCount="indefinite" />
        <animate attributeName="opacity" values="1;.2;1" dur={`${2+i*0.3}s`} repeatCount="indefinite" />
      </circle>
    ))}
    {/* Carpet rolled - rolls slightly */}
    <g style={{ animation: 'carpet-roll 4s ease-in-out infinite' }}>
      {/* Outer roll */}
      <ellipse cx="100" cy="120" rx="68" ry="50" fill={BRAND_LIGHT} stroke={INK} strokeWidth="3" />
      {/* Pattern stripes */}
      <ellipse cx="100" cy="120" rx="55" ry="40" fill="none" stroke={BRAND} strokeWidth="3" strokeDasharray="6 4" />
      <ellipse cx="100" cy="120" rx="42" ry="30" fill="none" stroke={BRAND_DARK} strokeWidth="2.5" strokeDasharray="4 3" />
      <ellipse cx="100" cy="120" rx="28" ry="20" fill="none" stroke={BRAND} strokeWidth="2" strokeDasharray="3 3" />
      {/* Center */}
      <ellipse cx="100" cy="120" rx="14" ry="10" fill={BRAND} />
      <ellipse cx="100" cy="120" rx="6" ry="4" fill={BRAND_DARK} />
    </g>
    {/* Floor shadow */}
    <ellipse cx="100" cy="178" rx="70" ry="6" fill={INK} opacity=".1" />
  </svg>
);

// 6. Laundry Gorden - curtain with sway
export const CurtainIcon = () => (
  <svg viewBox="0 0 200 200" className="w-full h-full" aria-hidden="true">
    {/* Rod */}
    <rect x="20" y="40" width="160" height="10" rx="5" fill={INK} />
    <circle cx="25" cy="45" r="8" fill={BRAND_DARK} />
    <circle cx="175" cy="45" r="8" fill={BRAND_DARK} />
    {/* Left curtain */}
    <g style={{ transformOrigin: '60px 50px', animation: 'curtain-sway 3.5s ease-in-out infinite' }}>
      <path d="M 30 50 Q 35 100 30 160 Q 50 165 50 160 Q 55 100 50 50 Z" fill={BRAND} stroke={INK} strokeWidth="3" strokeLinejoin="round" />
      <path d="M 50 50 Q 55 100 50 165 Q 70 170 70 160 Q 75 100 70 50 Z" fill={BRAND_LIGHT} stroke={INK} strokeWidth="3" strokeLinejoin="round" />
      <path d="M 70 50 Q 75 100 70 160 Q 90 165 90 160 Q 95 100 90 50 Z" fill={BRAND} stroke={INK} strokeWidth="3" strokeLinejoin="round" />
    </g>
    {/* Right curtain */}
    <g style={{ transformOrigin: '140px 50px', animation: 'curtain-sway-r 3.5s ease-in-out infinite' }}>
      <path d="M 110 50 Q 105 100 110 160 Q 130 165 130 160 Q 125 100 130 50 Z" fill={BRAND} stroke={INK} strokeWidth="3" strokeLinejoin="round" />
      <path d="M 130 50 Q 125 100 130 160 Q 150 165 150 160 Q 145 100 150 50 Z" fill={BRAND_LIGHT} stroke={INK} strokeWidth="3" strokeLinejoin="round" />
      <path d="M 150 50 Q 145 100 150 160 Q 170 165 170 160 Q 165 100 170 50 Z" fill={BRAND} stroke={INK} strokeWidth="3" strokeLinejoin="round" />
    </g>
    {/* Window glow in middle */}
    <rect x="92" y="60" width="16" height="100" fill="#FEF3C7" opacity=".5" rx="4" />
  </svg>
);

// 7. Laundry Sprei - folded sheet stack
export const SheetIcon = () => (
  <svg viewBox="0 0 200 200" className="w-full h-full" aria-hidden="true">
    {/* Floating heart/sparkles */}
    {[{x:35,y:50,d:0},{x:165,y:60,d:.5}].map((s,i)=>(
      <g key={i} style={{ animation: `sparkle 2.4s ${s.d}s ease-in-out infinite` }}>
        <circle cx={s.x} cy={s.y} r="4" fill={BRAND} opacity=".6" />
      </g>
    ))}
    {/* Stack of folded sheets - each layer pulses subtly */}
    <g style={{ animation: 'sheet-stack 3s ease-in-out infinite' }}>
      {/* Bottom layer */}
      <rect x="40" y="135" width="120" height="28" rx="4" fill={BRAND_DARK} stroke={INK} strokeWidth="3" />
      <line x1="100" y1="135" x2="100" y2="163" stroke={INK} strokeWidth="2" opacity=".4" />
      {/* Middle layer */}
      <rect x="45" y="110" width="110" height="26" rx="4" fill={BRAND} stroke={INK} strokeWidth="3" />
      <line x1="100" y1="110" x2="100" y2="136" stroke={INK} strokeWidth="2" opacity=".4" />
      {/* Top layer */}
      <rect x="50" y="85" width="100" height="26" rx="4" fill={BRAND_LIGHT} stroke={INK} strokeWidth="3" />
      <line x1="100" y1="85" x2="100" y2="111" stroke={INK} strokeWidth="2" opacity=".4" />
      {/* Ribbon */}
      <rect x="92" y="80" width="16" height="90" rx="2" fill="#FFD166" stroke={INK} strokeWidth="2" />
    </g>
    {/* Bow on top */}
    <path d="M 88 82 Q 100 72 112 82 Q 100 78 88 82" fill="#FFD166" stroke={INK} strokeWidth="2" strokeLinejoin="round" />
  </svg>
);

// 8. Laundry Bantal & Selimut - pillow with bounce
export const PillowIcon = () => (
  <svg viewBox="0 0 200 200" className="w-full h-full" aria-hidden="true">
    {/* Z's for sleep theme */}
    <text x="40" y="55" fill={BRAND} fontSize="22" fontWeight="bold" style={{ animation: 'zzz-1 2s ease-in-out infinite' }}>z</text>
    <text x="55" y="40" fill={BRAND_DARK} fontSize="18" fontWeight="bold" style={{ animation: 'zzz-2 2s 0.4s ease-in-out infinite' }}>z</text>
    <text x="155" y="55" fill={BRAND} fontSize="22" fontWeight="bold" style={{ animation: 'zzz-1 2s 0.6s ease-in-out infinite' }}>z</text>
    {/* Blanket fold (back) */}
    <g style={{ animation: 'pillow-bounce-slow 3s ease-in-out infinite' }}>
      <path d="M 25 160 Q 30 130 50 130 L 150 130 Q 170 130 175 160 L 170 175 L 30 175 Z" fill={BRAND} stroke={INK} strokeWidth="3" strokeLinejoin="round" />
      <path d="M 40 145 L 50 145 M 70 145 L 80 145 M 100 145 L 110 145 M 130 145 L 140 145" stroke={BRAND_DARK} strokeWidth="2.5" strokeLinecap="round" />
    </g>
    {/* Pillow on top - bounces */}
    <g style={{ transformOrigin: '100px 110px', animation: 'pillow-bounce 2s ease-in-out infinite' }}>
      <path d="M 45 115 Q 50 70 100 75 Q 150 70 155 115 Q 150 130 100 130 Q 50 130 45 115 Z" fill="#fff" stroke={INK} strokeWidth="3" strokeLinejoin="round" />
      {/* Pillow detail */}
      <circle cx="80" cy="100" r="3" fill={BRAND} />
      <circle cx="120" cy="100" r="3" fill={BRAND} />
      <path d="M 85 115 Q 100 122 115 115" fill="none" stroke={BRAND} strokeWidth="2.5" strokeLinecap="round" />
    </g>
  </svg>
);

export const ICON_MAP = {
  'Daily Kiloan': WashingMachineIcon,
  'Cuci & Setrika': IronIcon,
  'Laundry Bed Cover': BedCoverIcon,
  'Laundry Tas': BagIcon,
  'Laundry Karpet': CarpetIcon,
  'Laundry Gorden': CurtainIcon,
  'Laundry Sprei': SheetIcon,
  'Laundry Bantal & Selimut': PillowIcon,
};

const AnimatedLaundryIcon = ({ name }) => {
  const Icon = ICON_MAP[name] || WashingMachineIcon;
  return <Icon />;
};

export default AnimatedLaundryIcon;
